import { useQuery } from "@tanstack/react-query";
import { getCoachingNotes } from "@/lib/api";
import { useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";

/**
 * The active scope's coaching notes for a single match — the data
 * behind Coach Review's timeline chip rail.
 *
 * `getCoachingNotes(matchId)` narrows to `?match_id=` on the backend
 * (`coach_notes.coach_list_notes`), which still applies the
 * team-scope check. The query key carries `match_id` in the `extra`
 * slot so each match's notes get their own cache bucket and the
 * `invalidateScope` predicate still sees a `[domain, teamId,
 * seasonId, …]` tuple — a save (`useSaveCoachingNote`) invalidates
 * the whole scope, so the rail refetches after a new note lands.
 *
 * `enabled` is gated on BOTH `teamId` (scope must be reconciled —
 * otherwise the request 409s) and `matchId` (no match selected →
 * nothing to fetch; the rail renders its own no-match empty state
 * without a request).
 */
export function useCoachNotes(matchId: string | undefined) {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery({
    queryKey: useScopedKey("coaching-notes", { match_id: matchId ?? null }),
    queryFn: () => getCoachingNotes(matchId),
    enabled: !!teamId && !!matchId,
  });
}
